import {
  isProfileLoaded as defaultIsProfileLoaded,
  loadProfile as defaultLoadProfile,
  needsOnboarding as defaultNeedsOnboarding,
} from '@/composables/profile';
import { isAuthPageName, isSafeRedirectPath } from '@/utils/authRedirect';

/**
 * Onboarding guard — redirects a freshly-authenticated user whose stored
 * onboarding state is still `pending` to the dedicated funnel.
 *
 * It only ever redirects *to* `/onboarding`; it never blocks navigation away
 * from it, so a user can always `Skip`/`Complete` (which flip the status out of
 * `pending`) and land on Home. The funnel itself, the login/auth pages, and
 * any unauthenticated route are exempt — otherwise the user could be trapped.
 *
 * `?mode=edit` is non-blocking: an edit-mode visit is only reachable once
 * onboarding is no longer `pending`, so `needsOnboarding()` is false and the
 * guard does nothing.
 */
export function createOnboardingGuard({
  authStore,
  needsOnboarding = defaultNeedsOnboarding,
  loadProfile = defaultLoadProfile,
  isProfileLoaded = defaultIsProfileLoaded,
}) {
  return async (to) => {
    // API-only tenants skip the general onboarding: their first step is the
    // "create your first API key" card on the API overview.
    if (!authStore.isAuthenticated || authStore.isApiOnly) {
      return undefined;
    }

    // Never trap the funnel or the auth pages.
    if (to.name === 'onboarding' || to.path === '/onboarding' || isAuthPageName(to.name)) {
      return undefined;
    }

    if (!isProfileLoaded()) {
      await loadProfile();
    }

    if (needsOnboarding()) {
      // Remember where they were headed (an article after checkout, a tool...): the funnel
      // sends them on there when they finish or skip.
      const wanted = to.fullPath;
      return isSafeRedirectPath(wanted) && wanted !== '/'
        ? { path: '/onboarding', query: { redirect: wanted } }
        : { path: '/onboarding' };
    }

    return undefined;
  };
}
